import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Flex, IconButton, Portal, Text } from '@chakra-ui/react';
import { GripVertical, X } from 'lucide-react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

// Stacking level for menus and panels that must float above the ReactFlow
// canvas. The canvas, containers and node frames sit at 1000-1012 and react-
// select portals at 9999, so this clears everything in the editor. The exact
// number is otherwise arbitrary; the menu MenuLists import it so they and the
// panel share one source of truth. The panel's backdrop sits one below.
export const MENU_Z_INDEX = 10015;
const BACKDROP_Z_INDEX = MENU_Z_INDEX - 1;

// The "+" trigger button's top-right corner, which the panel pins its top-left
// to. The button stays put regardless of which way the menu expanded, so this
// is stable for both upward- and downward-opening menus.
export type AnchorRect = { top: number; right: number };

// The top-right of the menu's "+" trigger button. Pass the button element
// directly (e.g. from its ref); the menu list's rect is unreliable here because
// it stretches far down/up when open and would place the panel miles from it.
export const rectToAnchor = (el: Element | null): AnchorRect | null => {
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  return { top: rect.top, right: rect.right };
};

// Pin the panel's top-left just off the "+" button's top-right, so it always
// opens right beside the button the user clicked. Nudge back onto screen only
// if it would overflow the right or bottom edge.
const panelPosition = (anchor: AnchorRect | null, width: number, height: number) => {
  if (!anchor) {
    return { top: 0, left: 0 };
  }
  const gap = 8;
  const left = Math.max(gap, Math.min(anchor.right + gap, window.innerWidth - width - gap));
  const top = Math.max(gap, Math.min(anchor.top, window.innerHeight - height - gap));
  return { top, left };
};

type AnchoredPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  anchorRect: AnchorRect | null;
  width: number;
  // Optional: a short label is enough for these panels; the input placeholders
  // already say what they do, so the title can be omitted.
  title?: string;
  children: ReactNode;
};

/**
 * A portalled panel anchored to where an add-block menu opened. Escapes the
 * ReactFlow canvas transform (portal + high zIndex), clamps to the viewport,
 * dismisses on backdrop click or Escape, and shares a draggable header (grip,
 * title, close) so every insert picker looks and behaves the same.
 */
export function AnchoredPanel({
  isOpen,
  onClose,
  anchorRect,
  width,
  title,
  children,
}: AnchoredPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  // Once the user drags the panel we switch from anchor-derived positioning to
  // this explicit position; reset when it closes.
  const [dragPosition, setDragPosition] = useState<{ top: number; left: number } | null>(null);
  const dragState = useRef<{ startX: number; startY: number; top: number; left: number } | null>(
    null,
  );

  useEffect(() => {
    if (isOpen && panelRef.current) {
      setPanelHeight(panelRef.current.offsetHeight);
    }
  }, [isOpen, children]);

  useEffect(() => {
    if (!isOpen) {
      setDragPosition(null);
    }
  }, [isOpen]);

  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      top: rect?.top ?? 0,
      left: rect?.left ?? 0,
    };
    const onMove = (ev: PointerEvent) => {
      if (!dragState.current) {
        return;
      }
      setDragPosition({
        top: dragState.current.top + (ev.clientY - dragState.current.startY),
        left: dragState.current.left + (ev.clientX - dragState.current.startX),
      });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!isOpen) {
    return null;
  }

  const { top, left } = dragPosition ?? panelPosition(anchorRect, width, panelHeight);

  return (
    <Portal>
      <Box
        className="nodrag nopan nowheel"
        position="fixed"
        inset={0}
        zIndex={BACKDROP_Z_INDEX}
        onClick={onClose}
      />
      <Box
        ref={panelRef}
        className="nodrag nopan nowheel"
        position="fixed"
        top={pxToRem(top)}
        left={pxToRem(left)}
        zIndex={MENU_Z_INDEX}
        w={pxToRem(width)}
        bg="bg.surface"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        boxShadow="lg"
        p={2}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        <Flex align="center" gap={1} mb={2}>
          <Box
            as="button"
            aria-label="Drag to move"
            onPointerDown={onDragStart}
            cursor="grab"
            color="fg.muted"
            display="flex"
            alignItems="center"
            style={{ touchAction: 'none' }}
          >
            <GripVertical size={iconSize.sm} />
          </Box>
          <Text flex="1" fontSize="sm" fontWeight="medium" color="fg.muted">
            {title}
          </Text>
          <IconButton
            aria-label="Close"
            size="sm"
            variant="ghost"
            icon={<X size={iconSize.sm} />}
            onClick={onClose}
          />
        </Flex>
        {children}
      </Box>
    </Portal>
  );
}
